import React, { useCallback, useEffect, useState } from 'react'
import EditIcon from '@mui/icons-material/EditOutlined'

import styled from 'styled-components'

import { Panel } from 'components/Panel'
import ColumnGridPanel from 'components/ColumnGridPanel'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import LastUpdated from 'pages/Device/LastUpdated'
import OutdoorUnit from 'pages/Device/SystemConfigPanel/OutdoorUnit'
import IndoorUnit from 'pages/Device/SystemConfigPanel/IndoorUnit'
import Accessories from 'pages/Device/SystemConfigPanel/Accessories'
import Lockouts from 'pages/Device/SystemConfigPanel/Lockouts'

import useNexiaStream from 'hooks/useNexiaStream'
import useSystemConfiguration from 'hooks/useSystemConfiguration'

import { SystemConfigReport, SystemConfiguration } from 'types/SystemConfigReport'

import GA4 from 'helpers/ga4'
import { NoData, PanelHeaderPrimaryButton, PanelHeaderSecondaryButton } from 'styles/shared'

import Api from 'services/api'
import Apptentive from 'helpers/apptentive'
import { color } from 'styles/colors'

export type Props = {
  deviceId: string
  deviceType: string
  readOnly: boolean
  reportCache: object
}

const defaultSystemConfiguration: SystemConfiguration = {
  electricHeatKwPerStage: null,
  filterLocation: null,
  gasFurnaceBtuhPerStage: null,
  gasFurnaceEfficiency: null,
  indoorUnitCoilTonnage: null,
  outdoorUnitTonnage: null,
  userSelectedRefrigerantType: null,
  shutdownDevices: []
}

const SystemConfigPanel = ({ deviceId, deviceType, reportCache, readOnly }: Props) => {
  const { eventData: report } = useNexiaStream(deviceId, 'sys_config', reportCache)
  const { systemConfiguration, areSystemConfigurationLoaded } = useSystemConfiguration(deviceId)

  const [isEditing, setIsEditing] = useState(false)
  const [updatedSystemConfig, setUpdatedSystemConfig] = useState<SystemConfiguration>(defaultSystemConfiguration)

  useEffect(() => {
    if (areSystemConfigurationLoaded) {
      setUpdatedSystemConfig(systemConfiguration || defaultSystemConfiguration)
    }
  }, [areSystemConfigurationLoaded, systemConfiguration])

  const isLoaded = report !== null && areSystemConfigurationLoaded

  const onSaveConfiguration = useCallback(async (deviceId: string, modifiedSystemConfig: SystemConfiguration) => {
    const hasInvalidDevices = modifiedSystemConfig.shutdownDevices.filter(r => !r.deviceType || !r.wiringMethod).length

    if (!hasInvalidDevices) {
      await Api.SystemConfig.update(deviceId, modifiedSystemConfig)
      const eventData = { category: 'Customer Detail Page', action: 'Click Saved Config Edits', label: 'System Config Saved' }

      GA4.sendEvent(eventData)
    }
  }, [])

  const updateSystemConfig = useCallback(async (updatedSystemConfigAttributes: Partial<SystemConfiguration>) => {
    const modifiedSystemConfig: SystemConfiguration = { ...updatedSystemConfig, ...updatedSystemConfigAttributes }

    setUpdatedSystemConfig(modifiedSystemConfig)
  }, [updatedSystemConfig])

  const onCancelClick = () => {
    setIsEditing(false)
    setUpdatedSystemConfig(systemConfiguration || defaultSystemConfiguration)
  }

  const onSaveClick = useCallback(async () => {
    if (updatedSystemConfig !== defaultSystemConfiguration) {
      onSaveConfiguration(deviceId, updatedSystemConfig)
      Apptentive.sendEvent('system_config_done')
    }
    setIsEditing(false)
  }, [deviceId, updatedSystemConfig, onSaveConfiguration])

  const refrigerantType = report?.refrigerantType || updatedSystemConfig.userSelectedRefrigerantType

  const props = {
    isEditing,
    ...(report || {}) as SystemConfigReport,
    ...updatedSystemConfig,
    onChangeCallback: updateSystemConfig,
    refrigerantType,
    deviceType
  }

  return (
    <S.Panel pingdomId="sys-config-container" id="system-config" scrollToSection>
      <S.PanelHeader>
        <S.PanelHeaderLeft>
          <Panel.Title>System Config</Panel.Title>
          {!(readOnly) &&
              (!isEditing
                ? <Button decoration="primary" styles={PanelHeaderSecondaryButton} onPress={() => setIsEditing(true)}><S.ButtonText><S.EditIcon /><span>Edit</span></S.ButtonText></Button>
                : (
                  <>
                    <Button decoration="secondary" styles={PanelHeaderSecondaryButton} text="Cancel" onPress={onCancelClick} />
                    <Button decoration="positive" styles={PanelHeaderPrimaryButton} text="Save" onPress={onSaveClick} />
                  </>
                ))
            }
        </S.PanelHeaderLeft>
        {
          isLoaded && report.lastUpdatedAt && (
            <LastUpdated date={report.lastUpdatedAt} />
          )
        }
      </S.PanelHeader>
      {
        !isLoaded
          ? <S.NoData><h1>No Data Received Yet</h1></S.NoData>
          : (
            <ColumnGridPanel>
              <OutdoorUnit {...props} readOnlyRefrigerantType={!!report?.refrigerantType} />
              <IndoorUnit {...props} autoFocus={!(report.outdoorUnitType && report.outdoorUnitType.toLowerCase() !== 'none')} />
              <Accessories {...props} />
              <Lockouts {...props} />
            </ColumnGridPanel>
          )
      }
    </S.Panel>
  )
}

export default SystemConfigPanel

const S = {
  NoData: styled(NoData)`
    h1 {
      font-size: 28px;
    }
  `,
  Panel: styled(Panel)`
    scroll-margin-top: 60px;
  `,
  PanelHeaderLeft: styled(Panel.Header.Left)`
    display: flex;
    align-items: center;
  `,
  PanelHeader: styled(Panel.Header)`
    padding-right: 4px;
  `,
  ButtonText: styled.div`
  color: ${color('primaryButton')};
  font-weight: 700;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 5px;
  &:hover {
    color: ${color('primaryButtonHover')};
  }
`,
  EditIcon: styled(EditIcon)`
    font-size: 1.25em;
  `
}
